import { z } from 'zod'

/**
 * The lifecycle of one opportunity: a page of somebody else's, a page of ours
 * that answers something on it, and a reason to write.
 *
 * One human gate and it is the draft. Everything before `ready` is the
 * machine's — found, read, judged and written — because a triage list of
 * several hundred domains is work nobody does, and what is left of it after a
 * model has read the page is a handful of emails a day that a person can
 * actually read.
 *
 * `dismissed` is a judgement, by the model or by a person, and it carries a
 * reason either way. Bounces and unsubscribes close as `lost` with the reason
 * in `closeReason`: the suppression list is what stops the next mail, and a
 * status per way of ending would be a board with seven columns nobody reads.
 */
export const OUTREACH_PROSPECT_STATUSES = [
  'candidate',
  'researching',
  'ready',
  'dismissed',
  'approved',
  'contacted',
  'replied',
  'won',
  'lost',
  'archived',
] as const
export type OutreachProspectStatus = (typeof OUTREACH_PROSPECT_STATUSES)[number]

/**
 * Every status except `archived`, and the scope of the partial unique index on
 * `(site_id, referring_domain_id)`: one live pitch per domain per site. A
 * domain closed last year can be reconsidered as a new row once archived.
 */
export const OUTREACH_LIVE_STATUSES = [
  'candidate',
  'researching',
  'ready',
  'dismissed',
  'approved',
  'contacted',
  'replied',
  'won',
  'lost',
] as const satisfies readonly OutreachProspectStatus[]

/** The ones that are over, and therefore the ones the archive sweep retires. */
export const OUTREACH_CLOSED_STATUSES = [
  'dismissed',
  'lost',
  'won',
] as const satisfies readonly OutreachProspectStatus[]

/** Written to and not over: the threads somebody may still have to answer. */
export const OUTREACH_ACTIVE_STATUSES = [
  'contacted',
  'replied',
] as const satisfies readonly OutreachProspectStatus[]

/**
 * How a thread ended, as a person reads it. Stored as `won` and `lost` with the
 * detail in `closeReason`, because the worker already closes on those — a
 * bounce, an unsubscribe, a sequence that ran out — and three more statuses
 * would be three more places every job has to remember. `accepted` is `won`
 * whether a person said so or the backlink pull proved it; `wonAt` is only
 * ever the proof.
 */
export const OUTREACH_OUTCOMES = ['accepted', 'rejected', 'no-reply'] as const
export type OutreachOutcome = (typeof OUTREACH_OUTCOMES)[number]

/** The `closeReason`s on a `lost` row that are a no rather than a silence. */
export const OUTREACH_REJECTED_REASONS = ['declined', 'unsubscribed', 'wants-payment'] as const

/** What a person moving a thread writes. */
export const OUTREACH_OUTCOME_CLOSE = {
  accepted: { status: 'won', closeReason: 'accepted' },
  rejected: { status: 'lost', closeReason: 'declined' },
  'no-reply': { status: 'lost', closeReason: 'no-reply' },
} as const satisfies Record<
  OutreachOutcome,
  { status: OutreachProspectStatus; closeReason: string }
>

export function outreachOutcome(
  status: OutreachProspectStatus,
  closeReason: string | null,
): OutreachOutcome | null {
  if (status === 'won') return 'accepted'
  if (status !== 'lost') return null
  return (OUTREACH_REJECTED_REASONS as readonly string[]).includes(closeReason ?? '')
    ? 'rejected'
    : 'no-reply'
}

export const OUTREACH_MESSAGE_STATUSES = [
  'draft',
  'approved',
  'queued',
  'sent',
  'failed',
  'cancelled',
] as const
export type OutreachMessageStatus = (typeof OUTREACH_MESSAGE_STATUSES)[number]

export const OUTREACH_EVENT_KINDS = [
  'discovered',
  'researched',
  'drafted',
  'dismissed',
  'restored',
  'approved',
  'sent',
  'bounced',
  'replied',
  'auto_reply',
  'unsubscribed',
  'link_detected',
  'archived',
  'note',
] as const
export type OutreachEventKind = (typeof OUTREACH_EVENT_KINDS)[number]

/**
 * What came back. `auto_reply` is its own class because an out-of-office is
 * not a reply and must not stop the sequence, and `unknown` is a real answer.
 */
export const OUTREACH_REPLY_CLASSES = [
  'interested',
  'not_interested',
  'wants_payment',
  'auto_reply',
  'bounce',
  'unsubscribe',
  'unknown',
] as const
export type OutreachReplyClass = (typeof OUTREACH_REPLY_CLASSES)[number]

export const OUTREACH_CONTACT_SOURCES = ['scrape', 'manual'] as const
export type OutreachContactSource = (typeof OUTREACH_CONTACT_SOURCES)[number]

export const OUTREACH_SUPPRESSION_KINDS = ['email', 'domain'] as const
export type OutreachSuppressionKind = (typeof OUTREACH_SUPPRESSION_KINDS)[number]

/**
 * Why an opportunity was not worth a mail, whoever decided it.
 *
 * One list for the model and for a person, because the question it answers —
 * what does the machine keep getting wrong — is only a `group by` if both speak
 * the same words. The research gates use it too: a stale blog is `stale`
 * whether a date parser or a reader noticed.
 */
export const OUTREACH_DISMISS_REASONS = [
  'affiliate',
  'seller',
  'institution',
  'not-a-publisher',
  'off-topic',
  'no-target',
  'weak-fit',
  'stale',
  'unreachable',
  'unreadable',
  'already-linked',
  'sells-links',
  'spam',
  'other',
] as const
export type OutreachDismissReason = (typeof OUTREACH_DISMISS_REASONS)[number]

export const OUTREACH_DISMISS_LABELS: Record<OutreachDismissReason, string> = {
  affiliate: 'Earns from our market',
  seller: 'Sells in our market',
  institution: 'Too big to answer',
  'not-a-publisher': 'Not a publisher',
  'off-topic': 'Off topic',
  'no-target': 'Nothing of ours fits',
  'weak-fit': 'Weak fit',
  stale: 'Not updated',
  unreachable: 'No address',
  unreadable: 'Could not read',
  'already-linked': 'Already links',
  'sells-links': 'Sells links',
  spam: 'Spam',
  other: 'Other',
}

export const outreachProspectStatusSchema = z.enum(OUTREACH_PROSPECT_STATUSES)
export const outreachMessageStatusSchema = z.enum(OUTREACH_MESSAGE_STATUSES)
export const outreachEventKindSchema = z.enum(OUTREACH_EVENT_KINDS)
export const outreachReplyClassSchema = z.enum(OUTREACH_REPLY_CLASSES)
export const outreachContactSourceSchema = z.enum(OUTREACH_CONTACT_SOURCES)
export const outreachSuppressionKindSchema = z.enum(OUTREACH_SUPPRESSION_KINDS)
export const outreachDismissReasonSchema = z.enum(OUTREACH_DISMISS_REASONS)
export const outreachOutcomeSchema = z.enum(OUTREACH_OUTCOMES)
